/* aviaf — Alerts tab: triage board, incident threading, ack flow, escalation state, noise governance */
(() => {
const { useState } = React;
const S = () => window.OpsStore;
const isWarn = (a) => a.sev === "warn" || a.sev === "warning";

function AckControls({ a }) {
  useOps();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  if (a.ack) {
    const recent = Date.now() - a.ack.ts < 24 * 3600000;
    return recent && !a.ack.by.startsWith("dedup:") ? <button className="al-mini" onClick={() => S().unackAlert(a.pk)} title="journalled as incident_reopen">un-ack</button> : null;
  }
  if (asking) return (
    <span className="al-ackrow">
      <input className="ops-input sm" autoFocus placeholder="reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") S().ackAlert(a.pk, reason); if (e.key === "Escape") setAsking(false); }} />
      <button className="al-mini on" onClick={() => S().ackAlert(a.pk, reason)}>ack</button>
      <button className="al-mini" onClick={() => setAsking(false)}>×</button>
    </span>
  );
  return <button className="al-mini on" title={a.sev === "crit" ? "crit — human operators only (machine principals get 403)" : "acknowledge"} onClick={() => setAsking(true)}>ACK</button>;
}

function AlertCard({ a, dim }) {
  useOps(); window.useTicker(20000);
  const [open, setOpen] = useState(false);
  const slaDue = a.sev === "crit" && !a.ack && (Date.now() - a.ts) / 60000 > S().slaMinutes;
  const canOpenInc = !a.incident && !a.ack && a.sev === "crit" && (Date.now() - a.ts) / 60000 > 5;
  return (
    <div className="al-card" data-sev={a.sev === "warning" ? "warn" : a.sev} data-dim={dim}>
      <div className="al-l1">
        <window.SevChip sev={a.sev} />
        <span className="al-src mono" onClick={() => setOpen(!open)}>{a.source}/{a.code}</span>
        <span style={{ flex: 1 }} />
        {a.repeats.n > 1 && <span className="rep-chip" title={"dedupe group — grouping never resolves"}>×{a.repeats.n} since {window.fmtTs(a.repeats.since)}</span>}
        <span className="mono muted tnum al-age"><window.Age ts={a.ts} /></span>
      </div>
      <div className={"al-msg" + (open ? " open" : "")} onClick={() => setOpen(!open)}>{a.msg}</div>
      <div className="al-meta mono">
        <span className="mono muted">#{a.pk}</span>
        {a.instance && <span className="al-tag">{a.instance}</span>}
        {a.account && <span className="al-tag">{a.account}</span>}
        {a.incident && <a className="al-inc" href="#/ops" onClick={(e) => { e.preventDefault(); window.openIncidentDrawer(a.incident); }}>⧉ {a.incident}</a>}
        {slaDue && <span className="sla-chip">ESCALATION DUE &gt;{S().slaMinutes}m</span>}
        <span style={{ flex: 1 }} />
        {canOpenInc && <button className="al-mini" onClick={() => S().openIncident(a.pk)}>open incident</button>}
        <AckControls a={a} />
      </div>
      {a.ack && <div className="al-ackline mono">acked by <window.ActorChip actor={a.ack.by} /> · {window.fmtTs(a.ack.ts)}{a.ack.reason ? <span className="muted"> · “{a.ack.reason}”</span> : null}{a.ack.by.startsWith("dedup:") && <span className="muted" title="non-crit repeat within 24h inherited a HUMAN ack — crits are never birth-acked"> · inherited</span>}</div>}
      {open && a.sev === "crit" && a.incident && <div style={{ marginTop: 8 }}><window.DeliveryTrail incidentId={a.incident} /></div>}
    </div>
  );
}

function NoisePanel() {
  useOps(); window.useTicker(30000);
  const mutedIds = S().mutes.filter((m) => m.until > Date.now()).map((m) => m.source);
  return (
    <div className="noise-wrap">
      <div className="noise-grid">
        <div className="noise-hrow mono"><span>source</span><span className="num">24h</span><span>flood cap</span><span>taxonomy</span><span>mute</span></div>
        {S().noise.map((n) => {
          const muted = mutedIds.includes(n.source);
          return (
            <div key={n.source} className="noise-row mono" data-muted={muted}>
              <span className="ns-src">{n.source}</span>
              <span className="num tnum">{n.h24}</span>
              <span>{n.capped === true ? <b className="warn">CAPPED 8/h at source since {n.cappedAt} — suppressed count not visible to hub</b> : n.capped === false ? <span className="muted">under cap (8 warn/src/h)</span> : <span className="muted" title="the instance-side bridge caps 8 warn/source/hour but does not report cap state to the hub">cap state not reported to hub</span>}</span>
              <span>{n.legacy ? <b className="warn" title="source emits legacy severity 'warning' — treated as warn in UI, raw on hover">emits legacy “warning”</b> : <span className="muted">ok</span>}</span>
              <span>{muted
                ? <button className="al-mini" onClick={() => S().unmuteSource(n.source)}>unmute ({window.fmtAge(Date.now() - (S().mutes.find((m) => m.source === n.source).until - Date.now())).replace(/ .*/, "")} left)</button>
                : <button className="al-mini" title="UI-level, journalled, auto-expires 4h — never mutes crit" onClick={() => S().muteSource(n.source, 4)}>mute 4h</button>}</span>
            </div>
          );
        })}
      </div>
      <div className="noise-foot mono">instance-side bridge caps 8 warn/source/hour — the hub mirrors received counts only; suppressed alerts never reach it. Mutes are UI-level, journalled, auto-expiring, and never apply to crit.</div>
    </div>
  );
}

window.PageAlerts = function PageAlerts() {
  useOps(); window.useTicker(20000);
  const [showAllRecent, setShowAllRecent] = useState(false);
  const all = S().alerts;
  const mutedIds = S().mutes.filter((m) => m.until > Date.now()).map((m) => m.source);
  const openCrit = all.filter((a) => a.sev === "crit" && !a.ack).sort((a, b) => b.ts - a.ts);
  const openWarnAll = all.filter((a) => isWarn(a) && !a.ack).sort((a, b) => b.ts - a.ts);
  const openWarn = openWarnAll.filter((a) => !mutedIds.includes(a.source));
  const mutedN = openWarnAll.length - openWarn.length;
  const recent = all.filter((a) => a.ack || a.sev === "info").sort((a, b) => (b.ack ? b.ack.ts : b.ts) - (a.ack ? a.ack.ts : a.ts));
  const recentShown = showAllRecent ? recent : recent.slice(0, 6);
  return (
   <div className="ops-console">
    <div className="alerts2">
      <window.SectionHeader title="Alert triage" count={all.length}
        sub={`${openCrit.length} open crit · ${openWarnAll.length} open warn · ${recent.length} recent`}
        right={<>
          <button onClick={() => S().bulkAck("noncrit")} title="bulk-ack every open non-crit (warn+info) — never crit (§3.1); one server statement">bulk-ack non-crit</button>
        </>} />
      <div className="tri-board">
        <div className="tri-col" data-kind="crit">
          <div className="tri-h mono">OPEN CRIT <span className="tnum">{openCrit.length}</span><span className="tri-note">operator-ack only</span></div>
          {openCrit.map((a) => <AlertCard key={a.pk} a={a} />)}
          {openCrit.length === 0 && <div className="ops-empty mono">no open crits</div>}
        </div>
        <div className="tri-col" data-kind="warn">
          <div className="tri-h mono">OPEN WARN <span className="tnum">{openWarn.length}</span>{mutedN > 0 && <span className="tri-note">{mutedN} hidden by mute</span>}</div>
          {openWarn.map((a) => <AlertCard key={a.pk} a={a} />)}
          {openWarn.length === 0 && <div className="ops-empty mono">no open warns</div>}
        </div>
        <div className="tri-col" data-kind="recent">
          <div className="tri-h mono">RECENT · ACKED &amp; INFO <span className="tnum">{recent.length}</span></div>
          {recentShown.map((a) => <AlertCard key={a.pk} a={a} dim="true" />)}
          {recent.length > 6 && <button className="al-mini tri-more" onClick={() => setShowAllRecent(!showAllRecent)}>{showAllRecent ? "collapse" : `show all ${recent.length}`}</button>}
        </div>
      </div>
      <window.SectionHeader title="Incident threads" count={S().incidents.length} sub="alerts sharing an incident_id render as one timeline" right={<a className="mono" style={{ color: "var(--accent)", textDecoration: "none", fontSize: 11 }} href="#/ops">full timeline on Ops →</a>} />
      <div className="inc-strip">
        {S().incidents.length === 0 && <div className="ops-empty mono">no incidents yet — techops_actions have no incident_id groups</div>}
        {S().incidents.map((i) => {
          const m = window.opsIncidentMeta(i.id);
          if (!m) return null;
          return (
            <button key={i.id} className="inc-pill" data-open={m.status === "open"} onClick={() => window.openIncidentDrawer(i.id)}>
              <span className={"inc-dot " + (m.status === "open" ? (m.openCrit ? "neg" : "warn") : "ok")} />
              <span className="mono">{i.id}</span>
              <span className="ip-title">{i.title}</span>
              <span className="mono muted">{m.status === "open" ? <>open <window.Age ts={m.opened} /></> : "closed"} · {m.actionCount} actions</span>
              {m.stale && <span className="stale-chip">STALE</span>}
            </button>
          );
        })}
      </div>
      <window.SectionHeader title="Noise governance" sub="per-source 24h counts · flood-cap state · severity-taxonomy lint" />
      <NoisePanel />
    </div>
   </div>
  );
};
})();
